import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, IconButton, TextField } from '@mui/material';
import TvIcon from '@mui/icons-material/Tv';
import {
  DragIndicator, Edit as EditIcon, Check as CheckIcon, Close as CloseIcon, Lock,
} from '@mui/icons-material';
import { AndroidTvDevice, androidtvControl } from '../../services/androidtvApi';
import DeviceTypeImage from '../shared/DeviceTypeImage';
import PowerButton from '../shared/PowerButton';
import { DeviceIconWithBrandChip } from '../shared/DeviceBrandChip';
import { artifactTypeLineSx, sonosCardOuterSx } from '../../installed/music';
import { useAiDevicePolicy, useShowAiCardBadge } from '../../contexts/AiDevicePolicyContext';
import { requirePinIfNeeded } from '../../services/pinGate';
import {
  mapaAndroidTv,
  patchAndroidTvDevice,
  setAndroidTvPowerOptimistic,
} from '../../services/androidtvState';

const ACCENT = '#2391FF';
const CARD_BG = '#2F3235';

interface Props {
  device: AndroidTvDevice;
  onOpenModal?: (device: AndroidTvDevice) => void;
  onUpdated?: (d: AndroidTvDevice) => void;
  interactionLocked?: boolean;
  editMode?: boolean;
  isRoomEditing?: boolean;
  isEditingName?: boolean;
  dragHandleProps?: Record<string, unknown>;
  onStartEditName?: () => void;
  onSaveEditName?: (newName: string) => void;
  onCancelEditName?: () => void;
  showBrandChip?: boolean;
}

const AndroidTvDeviceCard: React.FC<Props> = ({
  device: deviceProp,
  onOpenModal,
  onUpdated,
  interactionLocked = false,
  editMode = false,
  isRoomEditing = false,
  isEditingName = false,
  dragHandleProps,
  onStartEditName,
  onSaveEditName,
  onCancelEditName,
  showBrandChip = false,
}) => {
  const { t } = useTranslation();
  const device = mapaAndroidTv[deviceProp.id] || deviceProp;
  const showAiCardBadge = useShowAiCardBadge();
  const aiPolicy = useAiDevicePolicy();
  const aiWhitelisted = showAiCardBadge && aiPolicy.isAndroidTvWhitelistedForIcon(device.id);
  const aiBlacklisted = showAiCardBadge && aiPolicy.isAndroidTvBlacklistedForIcon(device.id);
  const locked = interactionLocked || isRoomEditing || isEditingName;
  const canControl = Boolean(device.can_control) && !locked && !editMode;
  const showPower = !editMode && !locked && !isRoomEditing;
  const offline = !device.online;
  const hasArtefactoIcon = Boolean(device.artefacto?.nombre || device.artefacto_id);
  const tipoArtefacto = device.artefacto?.nombre || null;
  const [powerBusy, setPowerBusy] = useState(false);
  const [editingNameValue, setEditingNameValue] = useState(device.nombre);

  useEffect(() => {
    if (isEditingName) setEditingNameValue(device.nombre);
  }, [isEditingName, device.id, device.nombre]);

  const executeControl = async (action: string, value?: unknown) => {
    try {
      if (action === 'power') {
        setAndroidTvPowerOptimistic(device.id, !Boolean(device.is_on));
      }
      const res = await androidtvControl(device.id, action, value);
      if (res?.device) {
        if (action === 'power' && res.device.is_on != null) {
          setAndroidTvPowerOptimistic(device.id, Boolean(res.device.is_on));
        }
        patchAndroidTvDevice(res.device);
        onUpdated?.(mapaAndroidTv[device.id] || res.device);
      }
    } catch { /* noop */ }
  };

  const gated = (fn: () => void) => {
    if (!requirePinIfNeeded(device.pin_required, device.id, fn)) return;
    fn();
  };

  return (
    <Box
      sx={{
        ...sonosCardOuterSx,
        backgroundColor: CARD_BG,
        opacity: 1,
        cursor: editMode || locked ? 'default' : 'pointer',
        overflow: 'visible',
        // Scroll móvil (docs/51): igual que DeviceCard — no capturar touch en el root.
        pointerEvents: 'none',
        '& button, & .MuiIconButton-root, & .MuiButton-root, & [role="button"], & .MuiCheckbox-root, & .MuiInput-root, & [data-device-icon]': {
          pointerEvents: 'auto',
          touchAction: isRoomEditing ? 'none' : 'manipulation',
        },
      }}
    >
      {!isRoomEditing && !isEditingName && (
        <Box
          onClick={(e) => {
            if (editMode || locked) return;
            const target = e.target as HTMLElement;
            if (
              target.closest('button')
              || target.closest('.MuiIconButton-root')
              || target.closest('.MuiCheckbox-root')
              || target.closest('[data-device-icon]')
            ) {
              return;
            }
            const scrolled = (window as any).dashboardHasScrolledRef?.current;
            if (!scrolled) onOpenModal?.(device);
          }}
          sx={{
            position: 'absolute',
            top: 0,
            left: '80px',
            right: '120px',
            bottom: 0,
            pointerEvents: 'auto',
            touchAction: 'pan-y',
            zIndex: 1,
            cursor: 'pointer',
          }}
        />
      )}
      {/* Ícono — mismo marginRight que DeviceCard (Shelly) */}
      <DeviceIconWithBrandChip
        brand="androidtv"
        showBrandChip={showBrandChip}
        sx={{ marginRight: 1.5 }}
      >
        <Box
          data-device-icon
          sx={{
            touchAction: 'pan-y',
            pointerEvents: 'auto',
          }}
        >
          {hasArtefactoIcon ? (
            <DeviceTypeImage
              tipo="ANDROIDTV"
              tipo_artefacto={tipoArtefacto}
              iconOverride={device.artefacto?.icono ?? null}
              colorOverride={device.artefacto?.color_icono ?? ACCENT}
            />
          ) : (
            <Box sx={{
              width: 60, height: 60, borderRadius: '50%', bgcolor: '#1B1D21',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <TvIcon sx={{ fontSize: 28, color: ACCENT }} />
            </Box>
          )}
        </Box>
      </DeviceIconWithBrandChip>

      {/* Columna texto: flex:1 + pr 58px en edición = lápiz alineado con Samsung/Sonos (hueco del power). */}
      <Box sx={{
        flex: 1,
        overflow: 'hidden',
        minWidth: 0,
        position: 'relative',
        transform: 'translateY(-2px)',
        pr: (showPower || (editMode && !isRoomEditing)) ? '58px' : 0,
      }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: 0.15,
            position: 'relative',
            zIndex: isEditingName ? 100 : 'auto',
          }}
        >
          {isEditingName ? (
            <>
              <TextField
                value={editingNameValue}
                onChange={(e) => setEditingNameValue(e.target.value)}
                variant="standard"
                autoFocus
                onClick={(e) => e.stopPropagation()}
                sx={{
                  flex: 1,
                  '& .MuiInput-root': {
                    color: 'white',
                    fontSize: 14,
                    fontWeight: 'bold',
                  },
                  '& .MuiInput-underline:before': { borderBottomColor: '#666' },
                  '& .MuiInput-underline:after': { borderBottomColor: '#2391FF' },
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') onSaveEditName?.(editingNameValue);
                }}
              />
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); onSaveEditName?.(editingNameValue); }}
                sx={{
                  padding: 1,
                  marginLeft: 0.5,
                  minWidth: 32,
                  minHeight: 32,
                  zIndex: 101,
                  backgroundColor: 'rgba(35, 145, 255, 0.1)',
                }}
              >
                <CheckIcon sx={{ fontSize: 18, color: '#2391FF' }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); onCancelEditName?.(); }}
                sx={{
                  padding: 1,
                  marginLeft: 0.5,
                  minWidth: 32,
                  minHeight: 32,
                  zIndex: 101,
                  backgroundColor: 'rgba(255, 68, 68, 0.1)',
                }}
              >
                <CloseIcon sx={{ fontSize: 18, color: '#ff4444' }} />
              </IconButton>
            </>
          ) : (
            <>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 'bold',
                  color: '#fff',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                }}
              >
                {device.nombre}
              </Typography>
              {editMode && !isRoomEditing && onStartEditName && (
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); onStartEditName(); }}
                  sx={{
                    padding: 0.25,
                    marginLeft: 0.5,
                    flexShrink: 0,
                    '&:hover': { backgroundColor: 'rgba(35, 145, 255, 0.1)' },
                  }}
                >
                  <EditIcon sx={{ fontSize: 14, color: '#2391FF' }} />
                </IconButton>
              )}
            </>
          )}
        </Box>

        {tipoArtefacto ? (
          <>
            {/* 2.ª fila: reserva (paridad Shelly consumo / Sonos marquee). */}
            <Box sx={{ minHeight: 14 }} />
            {/* 3.er renglón: tipo de artefacto (misma tipografía que Shelly). */}
            <Typography sx={artifactTypeLineSx}>
              {tipoArtefacto}
            </Typography>
          </>
        ) : (
          /* Sin artefacto: misma reserva corta que la 2.ª fila Shelly. */
          <Box sx={{ minHeight: 14 }} />
        )}
      </Box>

      {offline && !isRoomEditing && (
        <Box sx={{ position: 'absolute', right: '80px', bottom: 6, zIndex: 100 }}>
          <Typography sx={{
            fontSize: 10, fontWeight: 'bold',
            padding: '0px 4.4px', borderRadius: '8.2px',
            color: '#ff4444',
            backgroundColor: 'rgba(255, 68, 68, 0.1)',
            border: '1px solid #ff4444',
            lineHeight: 1.0,
          }}>
            offline
          </Typography>
        </Box>
      )}

      {(Boolean(device.pin_required) || aiWhitelisted || aiBlacklisted) && (
        <Box sx={{
          position: 'absolute',
          right: '76px',
          bottom: 24,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: '4px',
          zIndex: 5,
          pointerEvents: 'none',
        }}>
          {Boolean(device.pin_required) && (
            <Lock sx={{ fontSize: 13, color: '#2391FF' }} />
          )}
          {aiWhitelisted && (
            <Typography
              component="span"
              aria-label={t('deviceList.aiPolicyAllowed') as string}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.62rem',
                fontWeight: 800,
                color: '#66bb6a',
                letterSpacing: '0.04em',
                lineHeight: 1,
              }}
            >
              {t('common.aiBadge')}
            </Typography>
          )}
          {aiBlacklisted && (
            <Typography
              component="span"
              aria-label={t('deviceList.aiPolicyDenied') as string}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.62rem',
                fontWeight: 800,
                color: '#ef5350',
                letterSpacing: '0.04em',
                lineHeight: 1,
              }}
            >
              <Box
                component="span"
                sx={{
                  textDecoration: 'line-through',
                  textDecorationColor: '#ef5350',
                  textDecorationThickness: '1.5px',
                }}
              >
                {t('common.aiBadge')}
              </Box>
            </Typography>
          )}
        </Box>
      )}

      {isRoomEditing && dragHandleProps && (
        <Box
          {...dragHandleProps}
          sx={{
            marginLeft: 0.5,
            transform: 'translateX(-10px)',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '40px',
            height: '40px',
            cursor: 'grab',
            pointerEvents: 'auto',
            touchAction: 'none',
          }}
        >
          <DragIndicator sx={{ color: 'white', fontSize: '2.25rem', pointerEvents: 'none' }} />
        </Box>
      )}

      {showPower && (
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            position: 'absolute',
            right: '10px',
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 2,
          }}
        >
          <PowerButton
            isOn={Boolean(device.is_on)}
            isUnknown={offline || !canControl || !device.paired}
            isLoading={powerBusy}
            deviceId={device.id}
            onClick={() => gated(() => {
              setPowerBusy(true);
              void executeControl('power', 'toggle').finally(() => setPowerBusy(false));
            })}
          />
        </Box>
      )}
    </Box>
  );
};

export default AndroidTvDeviceCard;
